<template>
  <div>
    <vx-card class="w-full">
      <div class="flex justify-between items-center">
        <h4>3단계 판단법(MIRAS) 엑셀 업로드</h4>
        <vs-button color="secondary" @click="onClickSave">등록</vs-button>
      </div>

      <!-- 기본정보 -->
      <field-select-module
        title="작성자 정보"
        v-model="form.stdInfo"
        :typeslabel="{
          date: '등록일',
        }"
        :board="board"
        :types="['department', 'com_name', 'rank', 'writer_name', 'date']"
      />

      <!-- 파일 업로드 -->
      <div>
        <div class="writeGridContainer">
          <div class="writeGridLineItem">
            <div>파일 업로드</div>
            <div>
              <vs-button color="secondary" type="border" :href="excelLink" download>엑셀 템플릿 다운로드</vs-button>
              <vs-button color="secondary" class="ml-2" @click="onClickExcelUpload">작성 엑셀 업로드</vs-button>
              <input v-show="false" ref="excelInput" type="file" multiple @change="onSelectFile" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" />
            </div>
          </div>
        </div>
        <p class="guide-text">
          ※ 위험도 등급은 <b>가능성(1~5) × 중대성(1~4)</b> 점수로 계산됩니다. — 1~6: <b>하</b> / 8~12: <b>중</b> / 13 이상: <b>상</b>
        </p>
      </div>

      <!-- 등록 대상 위험성평가 정보 -->
      <div>
        <div class="flex justify-between items-center mt-6 mb-4">
          <h5><i class="dot"></i> 등록 대상 위험성평가 정보</h5>
        </div>
        <div>
          <table>
            <colgroup>
              <col width="50" />
              <col width="60" />
              <col />
              <col />
              <col />
              <col width="160" />
              <col width="120" />
            </colgroup>
            <thead>
              <th></th>
              <th>No.</th>
              <th>회사명</th>
              <th>공정명</th>
              <th>작업내용</th>
              <th>작성일</th>
              <th>수정/삭제</th>
            </thead>
            <fragment v-for="(excel, excel_i) in excelData" :key="`위험성평가정보_${excel_i}`">
              <tbody>
                <tr :class="{ errLine: errorDataIndex.includes(excel_i) }">
                  <td class="cursor-pointer" @click="excel.open = !excel.open">
                    <img src="@/assets/icons/triangle_down.svg" class="mx-auto duration-200" :style="{ transform: excel.open ? 'rotate(0deg)' : 'rotate(-90deg)' }" />
                  </td>
                  <td>{{ excel_i + 1 }}</td>

                  <!-- 수정모드 -->
                  <template v-if="excel_i === editExcelIndex">
                    <td>
                      <div class="p-1">
                        <field-selector class="w-full" :field-name="editExcelData.회사명.field_name" @select="onSelectFieldSelector" />
                        <p v-if="editExcelData.errorMsg.회사명" class="errMsg">{{ editExcelData.errorMsg.회사명 }}</p>
                      </div>
                    </td>
                    <td>
                      <div class="p-1"><ml-input v-model="editExcelData.공정명" class="w-full" /></div>
                    </td>
                    <td>
                      <div class="p-1"><ml-input v-model="editExcelData.작업내용" class="w-full" /></div>
                    </td>
                    <td>
                      <div class="p-1">
                        <DateSelector v-model="editExcelData.작성일" class="w-full" />
                        <p v-if="editExcelData.errorMsg.작성일" class="errMsg">{{ editExcelData.errorMsg.작성일 }}</p>
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center gap-3">
                        <button @click="onClickFinishExcelData(excel_i)">
                          <feather-icon icon="CheckIcon" svgClasses="h-5 w-5" />
                        </button>
                        <button @click="onClickCancelExcelData()">
                          <feather-icon icon="XIcon" svgClasses="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </template>

                  <!-- 조회모드 -->
                  <template v-else>
                    <td>
                      {{ excel.회사명.field_name }}
                      <p v-if="excel.errorMsg.회사명" class="errMsg">{{ excel.errorMsg.회사명 }}</p>
                    </td>
                    <td>{{ excel.공정명 }}</td>
                    <td>{{ excel.작업내용 }}</td>
                    <td>
                      {{ excel.작성일 }}
                      <p v-if="excel.errorMsg.작성일" class="errMsg">{{ excel.errorMsg.작성일 }}</p>
                    </td>
                    <td>
                      <div class="flex justify-center gap-3">
                        <button :disabled="editExcelIndex !== null" @click="onClickEditExcelData(excel_i)">
                          <feather-icon icon="Edit2Icon" svgClasses="h-5 w-5" />
                        </button>
                        <button :disabled="editExcelIndex !== null" @click="onClickRemoveExcelData(excel_i)">
                          <feather-icon icon="Trash2Icon" svgClasses="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </template>
                </tr>

                <!-- 상세 -->
                <tr v-if="excel.open">
                  <td class="p-0 border-none" style="background: #fafafa" colspan="7">
                    <!-- ❶ 위험성평가표 -->
                    <div class="p-4 pr-0 pb-0">
                      <div class="pl-2 mb-2 flex justify-between items-center">
                        <h5 style="font-size: 14px"><i class="dot" style="background: #2c2c2ccb"></i> 위험성평가표</h5>
                      </div>
                      <table class="sub-table border-bottom" style="background: #fff">
                        <colgroup>
                          <col width="130" />
                          <col width="130" />
                          <col />
                          <col width="130" />
                          <col width="70" />
                          <col width="70" />
                          <col width="70" />
                          <col width="80" />
                          <col />
                          <col width="110" />
                          <col width="120" />
                        </colgroup>
                        <tr>
                          <th class="border-top" rowspan="2">①공종분류</th>
                          <th class="border-top" rowspan="2">②단위작업</th>
                          <th class="border-top" rowspan="2">③위험요인</th>
                          <th class="border-top" rowspan="2">재해형태</th>
                          <th class="border-top" colspan="4">④위험성 결정</th>
                          <th class="border-top" rowspan="2">⑤위험방지대책</th>
                          <th class="border-top" rowspan="2">⑥개선조치 담당자</th>
                          <th class="border-top" rowspan="2">조치예정일</th>
                        </tr>
                        <tr>
                          <th>가능성</th>
                          <th>중대성</th>
                          <th>위험점수</th>
                          <th>위험도 등급</th>
                        </tr>

                        <tr v-for="(risk, risk_i) in currentRiskList(excel, excel_i)" :key="`위험성평가표_${excel_i}_${risk_i}`">
                          <template v-if="excel_i === editExcelIndex">
                            <td><ml-input v-model="risk.공종분류" class="w-full" /></td>
                            <td><ml-input v-model="risk.단위작업" class="w-full" /></td>
                            <td><ml-textarea v-model="risk.위험요인" rows="3" class="w-full mb-0" /></td>
                            <td><disaster-class-selector v-model="risk.재해형태" class="w-full" /></td>
                            <td>
                              <vs-select class="w-full" autocomplete v-model="risk.가능성" @change="onChange위험도(risk)">
                                <vs-select-item v-for="item in optionsNumber1" :key="`가능성_${item.value}`" :value="item.value" :text="item.label" />
                              </vs-select>
                            </td>
                            <td>
                              <vs-select class="w-full" autocomplete v-model="risk.중대성" @change="onChange위험도(risk)">
                                <vs-select-item v-for="item in optionsNumber2" :key="`중대성_${item.value}`" :value="item.value" :text="item.label" />
                              </vs-select>
                            </td>
                            <td>{{ risk.위험점수 }}</td>
                            <td :class="`grade grade--${gradeClass(risk.위험도등급)}`">{{ risk.위험도등급 }}</td>
                            <td><ml-textarea v-model="risk.위험방지대책" rows="3" class="w-full mb-0" /></td>
                            <td><ml-input v-model="risk.담당자" class="w-full" /></td>
                            <td>
                              <DateSelector v-model="risk.조치예정일" class="w-full" />
                              <p v-if="riskErrorMsg(excel, risk_i, '조치예정일')" class="errMsg">{{ riskErrorMsg(excel, risk_i, "조치예정일") }}</p>
                            </td>
                          </template>
                          <template v-else>
                            <td>{{ risk.공종분류 }}</td>
                            <td>{{ risk.단위작업 }}</td>
                            <td class="text-left px-2">{{ risk.위험요인 }}</td>
                            <td>{{ risk.재해형태 }}</td>
                            <td>{{ risk.가능성 }}</td>
                            <td>{{ risk.중대성 }}</td>
                            <td>{{ risk.위험점수 }}</td>
                            <td :class="`grade grade--${gradeClass(risk.위험도등급)}`">{{ risk.위험도등급 }}</td>
                            <td class="text-left px-2">{{ risk.위험방지대책 }}</td>
                            <td>{{ risk.담당자 }}</td>
                            <td>
                              {{ risk.조치예정일 }}
                              <p v-if="riskErrorMsg(excel, risk_i, '조치예정일')" class="errMsg">{{ riskErrorMsg(excel, risk_i, "조치예정일") }}</p>
                            </td>
                          </template>
                        </tr>
                      </table>
                    </div>

                    <!-- ❷ 위험성 결정, 감소대책 수립 및 실행계획 확인 -->
                    <div class="p-4 pr-0 pb-0">
                      <div class="pl-2 mb-2 flex justify-between items-center">
                        <h5 style="font-size: 14px"><i class="dot" style="background: #2c2c2ccb"></i> ➆ 위험성 결정, 감소대책 수립 및 실행계획 확인</h5>
                      </div>
                      <table class="sub-table border-bottom" style="background: #fff">
                        <tr>
                          <th class="border-top">소속명</th>
                          <th class="border-top">직급</th>
                          <th class="border-top">직책</th>
                          <th class="border-top">이름</th>
                        </tr>
                        <tr v-for="(manager, manager_i) in excel.확인자" :key="`확인자_${excel_i}_${manager_i}`">
                          <td>{{ manager.소속명 }}</td>
                          <td>{{ manager.직급 }}</td>
                          <td>{{ manager.직책 }}</td>
                          <td>{{ manager.이름 }}</td>
                        </tr>
                      </table>
                    </div>

                    <!-- ❸ 개선현황 -->
                    <div class="p-4 pr-0 border-bottom">
                      <div class="pl-2 mb-2 flex justify-between items-center">
                        <h5 style="font-size: 14px"><i class="dot" style="background: #2c2c2ccb"></i> 개선현황 (조치 전/후 사진)</h5>
                      </div>
                      <table class="sub-table border-top border-bottom" style="background: #fff">
                        <tr>
                          <th>조치 전 사진</th>
                          <th>조치 후 사진</th>
                        </tr>
                        <tr>
                          <td class="p-2" style="vertical-align: top">
                            <img v-for="(image, image_i) in excel.개선현황.전" :key="`before_${excel_i}_${image_i}`" :src="`data:image/png;base64,${image.base64}`" class="preview-image" />
                          </td>
                          <td class="p-2" style="vertical-align: top">
                            <img v-for="(image, image_i) in excel.개선현황.후" :key="`after_${excel_i}_${image_i}`" :src="`data:image/png;base64,${image.base64}`" class="preview-image" />
                          </td>
                        </tr>
                      </table>
                    </div>
                  </td>
                </tr>
              </tbody>
            </fragment>
          </table>
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import DateSelector from "@/components/selector/DateSelector";
import FieldSelector from "@/components/selector/FieldSelector";
import DisasterClassSelector from "@/components/selector/DisasterClassSelector.vue";
import moment from "moment";
import ExcelJS from "exceljs";
import HttpRequest from "@/util/HttpRequest";
import { LAYOUT } from "./ExcelLayout";
import { getRiskGroupByScore } from "@/util/RiskGroupUtil";

// 엑셀 서식의 섹션 표시자. 서식이 바뀌어 행 번호가 밀려도 이 표시자로 위치를 찾는다.
const SECTION_MARK = {
  risk: "❶",
  manager: "❷",
  photo: "❸",
};

export default {
  components: { DisasterClassSelector, FieldSelector, FieldSelectModule, DateSelector },
  data() {
    return {
      optionsNumber1: [
        { value: 1, label: "1" },
        { value: 2, label: "2" },
        { value: 3, label: "3" },
        { value: 4, label: "4" },
        { value: 5, label: "5" },
      ],
      optionsNumber2: [
        { value: 1, label: "1" },
        { value: 2, label: "2" },
        { value: 3, label: "3" },
        { value: 4, label: "4" },
      ],
      fields: [],
      board: {
        projectTitle: this.$store.state.project.projectInfo.field_name,
        writer: {}, //작성자 정보
        readonly: false, // 읽기전용인가
        isPrint: false,
        viewSaveBtn: true,
        viewRemoveBtn: true,
      },

      form: {
        stdInfo: {},
        fields: [],
        company: null,
      },

      editExcelIndex: null,
      editExcelData: {},
      excelData: [],
      errorDataIndex: [],
    };
  },
  computed: {
    projectId() {
      return Number(this.$route.params.proj_id);
    },
    boardId() {
      return this.$route.params.boardId;
    },
    myInfo() {
      return this.$store.state.member.memberInfo;
    },
    excelLink() {
      return "/fms/위험성평가_3등급_MIRAS.xlsx";
    },
  },
  async mounted() {
    await this.init();
  },
  methods: {
    async init() {
      await this.$store.dispatch("USER_INFO");
      this.board.writer = {
        id: this.$store.state.auth.userInfo.id,
        name: this.$store.state.auth.userInfo.name,
        company_id: this.$store.state.auth.userInfo.company_id,
        department: this.$store.state.auth.userInfo.department,
        section: this.$store.state.auth.userInfo.section,
        rank: this.$store.state.auth.userInfo.rank,
        com_name: "",
      };

      await this.$store.dispatch("company/LOAD_COMPANY", {
        companyId: this.board.writer.company_id,
      });
      this.board.writer.com_name = this.$store.state.company.companyInfo.com_name;
      if (!this.board.writer.section) {
        this.board.writer.section = this.board.writer.com_name;
      }

      await this.loadFieldListAll();
    },
    async loadFieldListAll() {
      this.loading(true);
      const loadFieldList = (page) => {
        this.$store
          .dispatch("project/LOAD_FIELD_LIST", {
            projectId: this.projectId,
            page,
            perPage: 50,
          })
          .then((response) => {
            const data = response.data;
            this.fields.push(...data);
            if (response.last_page > page) {
              loadFieldList(page + 1);
            } else {
              this.loading(false);
            }
          });
      };
      await loadFieldList(1);
    },

    // ── 위험도 등급 ─────────────────────────────────────────────
    // 가능성 × 중대성 점수로 상/중/하 를 계산한다 (RiskTable 과 동일 기준)
    onChange위험도(risk) {
      const score = Number(risk.가능성) * Number(risk.중대성);
      this.$set(risk, "위험점수", score || "");
      this.$set(risk, "위험도등급", getRiskGroupByScore(score));
    },
    gradeClass(grade) {
      if (grade === "상") return "high";
      if (grade === "중") return "mid";
      if (grade === "하") return "low";
      return "none";
    },
    currentRiskList(excel, excelIndex) {
      return excelIndex === this.editExcelIndex ? this.editExcelData.위험성평가표 : excel.위험성평가표;
    },
    riskErrorMsg(excel, riskIndex, key) {
      const errors = excel.errorMsg.위험성평가표;
      return errors && errors[riskIndex] ? errors[riskIndex][key] : "";
    },

    // ── 엑셀 읽기 ───────────────────────────────────────────────
    onClickExcelUpload() {
      const excelInput = this.$refs.excelInput;
      excelInput.value = null;
      excelInput.click();
    },
    onSelectFile(event) {
      for (const file of event.target.files) {
        this.readFile(file);
      }
    },
    // 시트 A열에서 섹션 표시자(❶/❷/❸)가 있는 행 번호를 찾는다
    findSectionRow(worksheet, mark) {
      for (let rowIndex = 1; rowIndex <= worksheet.rowCount; rowIndex++) {
        const value = worksheet.getRow(rowIndex).getCell(1).value;
        if (typeof value === "string" && value.trim().startsWith(mark)) {
          return rowIndex;
        }
      }
      return -1;
    },
    cellText(worksheet, row, col) {
      const value = worksheet.getRow(row).getCell(col).value;
      if (value === null || value === undefined) return "";
      // 수식 셀은 { formula, result } 형태로 들어온다
      if (typeof value === "object" && "result" in value) return `${value.result}`;
      if (typeof value === "object" && "text" in value) return `${value.text}`;
      if (value instanceof Date) return moment(value).format("YYYY-MM-DD");
      return `${value}`.trim();
    },
    async readFile(file) {
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(file);
      const media = workbook.model.media;

      for (const worksheet of workbook.worksheets) {
        const riskRow = this.findSectionRow(worksheet, SECTION_MARK.risk);
        const managerRow = this.findSectionRow(worksheet, SECTION_MARK.manager);
        const photoRow = this.findSectionRow(worksheet, SECTION_MARK.photo);

        // 서식이 아닌 시트(작성요령 등)는 건너뛴다
        if (riskRow < 0) continue;

        const images = worksheet.getImages().map((image) => {
          try {
            const medium = media.find((m) => m.index === image.imageId);
            return {
              rowIndex: image.range.tl.nativeRow + 1,
              columnIndex: image.range.tl.nativeCol + 1,
              base64: Buffer.from(medium.buffer).toString("base64"),
            };
          } catch (e) {
            return { rowIndex: -1, columnIndex: -1, base64: undefined };
          }
        });

        const info = LAYOUT.info;
        const obj = {
          회사명: {
            field_id: -1,
            field_name: this.cellText(worksheet, info.회사명.row, info.회사명.valueCol),
          },
          공정명: this.cellText(worksheet, info.공정명.row, info.공정명.valueCol),
          작성자: this.cellText(worksheet, info.작성자.row, info.작성자.valueCol),
          작업내용: this.cellText(worksheet, info.작업내용.row, info.작업내용.valueCol),
          작성일: this.cellText(worksheet, info.작성일.row, info.작성일.valueCol),
          위험성평가표: [],
          확인자: [],
          개선현황: { 전: [], 후: [] },
        };

        // ❶ 위험성평가표 : 표시자 다음 행이 헤더, 그 다음 행부터 데이터
        const riskEnd = managerRow > 0 ? managerRow - 1 : worksheet.rowCount;
        for (let rowIndex = riskRow + 2; rowIndex <= riskEnd; rowIndex++) {
          const 공종분류 = this.cellText(worksheet, rowIndex, 2);
          const 단위작업 = this.cellText(worksheet, rowIndex, 3);
          const 위험요인 = this.cellText(worksheet, rowIndex, 4);
          // No.만 채워진 빈 서식 행은 건너뛴다
          if (!공종분류 && !단위작업 && !위험요인) continue;

          const risk = {
            공종분류,
            단위작업,
            위험요인,
            재해형태: this.cellText(worksheet, rowIndex, 5),
            가능성: Number(this.cellText(worksheet, rowIndex, 6)) || "",
            중대성: Number(this.cellText(worksheet, rowIndex, 7)) || "",
            위험점수: "",
            위험도등급: this.cellText(worksheet, rowIndex, 9),
            위험방지대책: this.cellText(worksheet, rowIndex, 10),
            담당자: this.cellText(worksheet, rowIndex, 11),
            조치예정일: this.cellText(worksheet, rowIndex, 12),
            이행확인_근로자대표: this.isChecked(this.cellText(worksheet, rowIndex, 13)),
            이행확인_관리감독자: this.isChecked(this.cellText(worksheet, rowIndex, 14)),
          };

          // 가능성·중대성이 입력되어 있으면 등급은 항상 계산값을 따른다
          if (risk.가능성 && risk.중대성) {
            this.onChange위험도(risk);
          }

          obj.위험성평가표.push(risk);
        }

        // ❷ 확인자
        if (managerRow > 0) {
          const managerEnd = photoRow > 0 ? photoRow - 1 : worksheet.rowCount;
          for (let rowIndex = managerRow + 2; rowIndex <= managerEnd; rowIndex++) {
            const 소속명 = this.cellText(worksheet, rowIndex, 1);
            const 이름 = this.cellText(worksheet, rowIndex, 4);
            if (!소속명 && !이름) continue;

            obj.확인자.push({
              소속명,
              직급: this.cellText(worksheet, rowIndex, 2),
              직책: this.cellText(worksheet, rowIndex, 3),
              이름,
            });
          }
        }

        // ❸ 개선현황 : 사진이 붙은 컬럼 위치로 조치 전/후를 구분한다
        if (photoRow > 0) {
          const splitCol = LAYOUT.photo.beforeAfterSplitCol;
          for (const image of images) {
            if (image.rowIndex <= photoRow + 1 || !image.base64) continue;
            if (image.columnIndex < splitCol) obj.개선현황.전.push(image);
            else obj.개선현황.후.push(image);
          }
        }

        // 유효성 검사
        const errorMsg = {
          회사명: "",
          작성일: "",
          위험성평가표: [],
        };
        this.checkValue(obj, errorMsg);
        obj.errorMsg = errorMsg;
        this.excelData.push({ ...obj, open: false });
      }

      if (this.excelData.length === 0) {
        this.customAlert("서식에 맞는 시트를 찾지 못했습니다. 엑셀 템플릿을 다시 내려받아 작성해주세요");
      }
    },
    isChecked(value) {
      return ["O", "o", "ㅇ", "√", "V", "v", "Y", "y", "TRUE", "true"].includes(`${value}`.trim());
    },

    // ── 유효성 검사 ─────────────────────────────────────────────
    checkValue(obj, errorMsg) {
      this.__checkValueField("회사명", obj, errorMsg);
      this.__checkValueDate("작성일", obj, errorMsg);
      this.__checkValue위험성평가표("위험성평가표", obj, errorMsg);
    },
    __checkValueField(key, obj, errorMsg) {
      // 유효성 검사 : 사업장
      const findField = this.fields.find((field) => field.field_name === obj[key].field_name);
      if (findField) {
        obj[key].field_id = findField.id;
        obj[key].project_name = findField.project.field_name;
      } else {
        errorMsg[key] = "입력된 데이터가 검색되지 않습니다.";
      }
    },
    __checkValueDate(key, obj, errorMsg) {
      if (obj && obj[key]) {
        // 유효성 검사 : 날짜
        const isDate = obj[key] === "" ? true : !isNaN(new Date(obj[key]));
        if (!isDate) {
          errorMsg[key] = "날짜 형식이 아닙니다.";
        } else {
          obj[key] = moment(obj[key]).format("YYYY-MM-DD");
        }
      }
    },
    __checkValue위험성평가표(key, obj, errorMsg) {
      if (!obj || !obj[key]) return;

      for (let i = 0; i < obj[key].length; i++) {
        const sub = obj[key][i];
        if (!sub) continue;

        errorMsg[key][i] = { 조치예정일: "", 위험도등급: "" };
        this.__checkValueDate("조치예정일", sub, errorMsg[key][i]);

        if (!sub.위험도등급) {
          errorMsg[key][i].위험도등급 = "가능성·중대성을 입력하거나 위험도 등급(상/중/하)을 입력해주세요.";
        }
      }
    },

    // ── 목록 편집 ───────────────────────────────────────────────
    onClickEditExcelData(index) {
      this.editExcelData = JSON.parse(JSON.stringify(this.excelData[index]));
      this.editExcelIndex = index;
    },
    onClickFinishExcelData(excelIndex) {
      const editExcelData = { ...this.editExcelData };

      const errorMsg = {
        회사명: editExcelData.errorMsg.회사명,
        작성일: "",
        위험성평가표: [],
      };
      this.__checkValueDate("작성일", editExcelData, errorMsg);
      this.__checkValue위험성평가표("위험성평가표", editExcelData, errorMsg);
      editExcelData.errorMsg = errorMsg;

      // this.excelData[excelIndex] = editExcelData; 로 대입하면 주소값이 바뀌어
      // 펼침/닫힘 상태가 초기화되므로 키 단위로 덮어쓴다
      const keys = Object.keys(this.excelData[excelIndex]);
      for (const key of keys) {
        this.excelData[excelIndex][key] = editExcelData[key];
      }
      this.onClickCancelExcelData();
    },
    onClickCancelExcelData() {
      this.editExcelData = {};
      this.editExcelIndex = null;
    },
    onClickRemoveExcelData(index) {
      this.excelData.splice(index, 1);
    },
    onSelectFieldSelector(value) {
      if (value.field_name) {
        this.$set(this.editExcelData, "회사명", {
          field_id: value.id,
          field_name: value.field_name,
        });
        this.$set(this.editExcelData.errorMsg, "회사명", "");
      } else {
        this.$set(this.editExcelData, "회사명", {
          field_id: 0,
          field_name: "",
        });
        this.$set(this.editExcelData.errorMsg, "회사명", "입력된 데이터가 검색되지 않습니다.");
      }
    },

    // ── 등록 ────────────────────────────────────────────────────
    async onClickSave() {
      if (this.excelData.length < 1) {
        this.customAlert("등록 대상 위험성평가 정보가 없습니다");
        return;
      }
      if (this.editExcelIndex !== null) {
        this.customAlert("수정 중인 항목을 먼저 저장해주세요");
        return;
      }

      this.errorDataIndex = [];
      let check사업장 = false;

      for (let i = 0; i < this.excelData.length; i++) {
        const excel = this.excelData[i];

        if (excel.회사명.field_id <= 0) {
          check사업장 = true;
        }
        if (excel.errorMsg.회사명 || excel.errorMsg.작성일) {
          this.errorDataIndex.push(i);
          continue;
        }
        if (excel.errorMsg.위험성평가표.some((sub) => Object.values(sub).some((value) => value !== ""))) {
          this.errorDataIndex.push(i);
        }
      }

      if (this.errorDataIndex.length > 0) {
        if (check사업장) {
          this.customAlert("회사명이 입력되지 않은 데이터가 있습니다");
        } else {
          this.customAlert("입력된 데이터가 유효하지 않습니다");
        }
        return;
      }

      const posts = [];
      this.loading(true);

      try {
        for (const excel of this.excelData) {
          const writeDate = excel.작성일 || moment().format("YYYY-MM-DD");
          const header = {
            full: writeDate,
            month: Number(moment(writeDate).format("MM")),
            year: Number(moment(writeDate).format("YYYY")),
            date: writeDate,
          };

          const stdInfo = {
            ...this.form.stdInfo,
            project_name: excel.회사명.project_name,
            field_id: excel.회사명.field_id,
            field_name: excel.회사명.field_name,
            공정명: excel.공정명,
            작업내용: excel.작업내용,
          };

          // riskList : RiskTable 이 다루는 행 구조와 동일하게 맞춘다
          const riskList = excel.위험성평가표.map((risk, index) => ({
            id: `excel_${Date.now()}_${index}`,
            input1: risk.공종분류,
            input2: risk.단위작업,
            input3: risk.위험요인,
            input4: risk.재해형태,
            input5: risk.위험도등급,
            input6: risk.위험방지대책,
            input7: risk.담당자,
            input8: risk.조치예정일,
            input9: risk.이행확인_근로자대표,
            input10: risk.이행확인_관리감독자,
            평가구분: "",
            유해위험요인: "",
            가능성: risk.가능성,
            중대성: risk.중대성,
            위험점수: risk.위험점수,
          }));

          const managerList = excel.확인자.map((manager, index) => ({
            id: index + 1,
            target: manager.소속명,
            position: manager.직급,
            type: manager.직책,
            name: manager.이름,
            sign: "",
          }));

          const photoListBeforeAfter = await this.uploadPhotos(excel.개선현황);

          const body = {
            headerDate: `${header.year}-${`${header.month}`.padStart(2, "0")}`,
            title: this.board.writer.department,
            stdInfo,
            fileList: [],
            riskDisaster: riskList.map((item) => item.input4).join(", "),
            riskList,
            managerList,
            photoList: photoListBeforeAfter.map((item) => item.이전사진),
            photoListAfter: photoListBeforeAfter.map((item) => item.이후사진),
            photoListBeforeAfter,
            sign1: { name: "", sign: "" },
            sign2: { name: "", sign: "" },
            sign3: { name: "", sign: "" },
            sign4: { name: "", sign: "" },
          };

          posts.push({ title: "3단계 판단법(MIRAS)", content: JSON.stringify({ header, body }), projectId: this.projectId });
        }

        await HttpRequest.put(`/api/v1/board/post/${this.boardId}/multiple`, { posts });

        this.excelData = [];
        this.editExcelData = {};
        this.editExcelIndex = null;
        this.errorDataIndex = [];

        this.customAlert("위험성 평가가 등록되었습니다", "위험성 평가 등록");
      } catch (error) {
        console.error("엑셀 업로드 오류:", error);
        this.customAlert("서버에서 오류가 발생했습니다. 관리자에게 문의해주세요");
      } finally {
        this.loading(false);
      }
    },

    // 엑셀에 붙어 있던 이미지를 업로드하고 개선현황(이전/이후) 쌍으로 묶는다
    async uploadPhotos(개선현황) {
      const upload = async (images, label) => {
        const uploaded = [];
        for (let i = 0; i < images.length; i++) {
          const file = this.base64ToFile(images[i].base64, `boardId_${this.boardId}_${label}_${i}.png`);
          const response = await this.$store.dispatch("file/FILE_UPLOAD", file);
          uploaded.push({
            id: response.id,
            path: response.path,
            fileName: response.file_name,
            image: response,
            input: "",
          });
        }
        return uploaded;
      };

      const before = await upload(개선현황.전, "조치전사진");
      const after = await upload(개선현황.후, "조치후사진");

      const rows = [];
      const length = Math.max(before.length, after.length);
      for (let i = 0; i < length; i++) {
        rows.push({
          id: `list_${i}`,
          이전사진: before[i] ? before[i] : {},
          이후사진: after[i] ? after[i] : {},
        });
      }
      return rows;
    },
    base64ToFile(base64, fileName) {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new File([bytes], fileName, { type: "image/png" });
    },

    customAlert(msg, title) {
      const customAlert = new CustomEvent("customAlert", {
        detail: {
          title: title ? title : "오류가 발생했습니다",
          message: msg,
        },
      });
      window.dispatchEvent(customAlert);
    },
    loading(open) {
      const loadingDialog = new CustomEvent("loadingDialog", {
        detail: {
          open,
        },
      });
      window.dispatchEvent(loadingDialog);
    },
  },
};
</script>

<style scoped lang="scss">
table {
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    text-align: center;
    padding: 0;
  }

  th {
    font-weight: 700;
    font-size: 14px;
    line-height: 17px;

    background: #fafafa;
    border-top: 1px solid #cdcdcd;
    border-bottom: 1px solid #cdcdcd;
    padding: 16px 0;
  }

  td {
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;

    border-bottom: 1px solid #cdcdcd;
    padding: 16px 0;
  }

  .sub-table {
    border-left: 1px solid #cdcdcd;
    border-top: 0;
    border-bottom: 0;
    border-right: 0;

    th {
      background: #7e72f20d;
      padding: 16px 10px;
    }

    th,
    td {
      border-top: 0;
      border-left: 0;
      border-bottom: 0;
      border-right: 1px solid #cdcdcd;
    }

    tr {
      border-bottom: 1px solid #cdcdcd;

      &:last-child {
        border-bottom: 0;
      }
    }
  }

  button {
    width: 32px;
    height: 32px;

    display: flex;
    align-items: center;
    justify-content: center;

    color: #7e72f2;
    background: none;
    border: 1px solid #7e72f2;
    border-radius: 4px;
    cursor: pointer;

    span {
      margin: 0;
    }
  }

  .check {
    justify-content: center !important;

    ::v-deep {
      span {
        margin: 0;
      }
    }
  }
}

.guide-text {
  font-size: 13px;
  line-height: 20px;
  color: #626262;
  margin-top: 8px;
}

.preview-image {
  max-width: 100%;
  margin-bottom: 8px;
}

.grade {
  font-weight: 700;

  &--low {
    color: #28c76f;
  }
  &--mid {
    color: #ff8510;
  }
  &--high {
    color: #e42728;
  }
}

.errMsg {
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  text-align: center;
  color: #ea5455;
  margin-top: 2px;
}
.errLine {
  color: #ea5455;
}
</style>
